import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useApi } from '../ApiContext'; 
import Gallery from './Gallery'; 
import ApkManager from './ApkManager';
import './ManageDownload.css';

// =========================================================================
// DownloadDisplay Component (Presents the list/table)
// =========================================================================
const DownloadDisplay = ({ downloads, openModal, handleDeleteDownload }) => (
    <table className="data-table">
        <thead>
            <tr>
                <th>ID</th>
                <th>App Name</th>
                <th>Device Type</th>
                <th>Version</th>
                <th>Manual Description</th>
                <th>APK Description</th>
                <th>Actions</th>
            </tr>
        </thead>
        <tbody>
            {downloads.length === 0 ? (
                <tr>
                    <td colSpan="7" style={{ textAlign: 'center' }}>
                        No download items found. Add a new item to begin.
                    </td>
                </tr>
            ) : (
                downloads.map(item => (
                    <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>{item.application_name}</td>
                        <td>{item.device_type}</td>
                        <td>{item.apk_version}</td>
                        <td className="description-cell">{item.description || 'N/A'}</td>
                        <td className="description-cell">{item.apk_description || 'N/A'}</td>
                        <td className="actions-cell">
                            <button 
                                type="button" 
                                onClick={() => openModal('editDownload', item)}
                            >
                                Edit
                            </button>
                            <button 
                                type="button" 
                                onClick={() => handleDeleteDownload(item.id)}
                            >
                                Delete
                            </button>
                        </td>
                    </tr>
                ))
            )}
        </tbody>
    </table>
);

// =========================================================================
// ManageDownload Component (Main page logic)
// =========================================================================
const ManageDownload = () => {
    
    const { api } = useApi(); 
    
    const [downloads, setDownloads] = useState([]);
    const [modalType, setModalType] = useState(null);
    const [editingDownload, setEditingDownload] = useState(null);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [isApkManagerOpen, setIsApkManagerOpen] = useState(false);

    const [formData, setFormData] = useState({
        application_name: '',
        image_link: '',
        download_link: '',
        device_type: '',
        apk_version: '',
        description: '',
    });

    const deviceOptions = ['MOBILE', 'TV'];

    // --- Fetch API Data (READ) ---
    const fetchDownloads = useCallback(async () => {
        if (!api) return;
        try {
            const response = await api.get('/downloads');
            setDownloads(response.data);
        } catch (error) {
            console.error('❌ Error fetching downloads:', error);
            toast.error("Failed to load downloads.");
        }
    }, [api]);

    useEffect(() => {
        fetchDownloads();
    }, [fetchDownloads]); 

    // --- Modal & Form Logic ---
    const openModal = (type, itemToEdit = null) => {
        setModalType(type);
        setEditingDownload(null);
        
        let initialData = {
            application_name: '',
            image_link: '',
            download_link: '',
            device_type: '',
            apk_version: '',
            description: '',
        };

        if (itemToEdit) {
            setEditingDownload(itemToEdit);
            initialData = {
                application_name: itemToEdit.application_name || '',
                image_link: itemToEdit.image_link || '',
                download_link: itemToEdit.download_link || '',
                device_type: itemToEdit.device_type || '',
                apk_version: itemToEdit.apk_version?.toString() || '',
                description: itemToEdit.description || '',
            };
        }
        setFormData(initialData);
    };

    const closeModal = () => {
        setModalType(null);
        setEditingDownload(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({ ...prevData, [name]: value }));
    };

    const openGallery = () => { setIsGalleryOpen(true); };
    const closeGallery = () => { setIsGalleryOpen(false); };
    
    const handleImageSelect = (selectedImageUrl) => {
        setFormData(prevData => ({ ...prevData, image_link: selectedImageUrl }));
        closeGallery();
    };

    const openApkManager = () => { 
        setIsApkManagerOpen(true); 
    };
    const closeApkManager = () => { setIsApkManagerOpen(false); };
    
    const handleApkSelect = (downloadLink, version) => {
        setFormData(prevData => ({ 
            ...prevData, 
            download_link: downloadLink,
            apk_version: String(version ?? '')
        }));
        closeApkManager();
        toast.success(`APK v${version} link populated!`);
    };

    // --- API Submission Handlers ---
    // apk_version is sent as a STRING so decimals / multi-part versions
    // (1.5, 2.10, 1.2.3) are preserved exactly. Requires Sys_Download.apk_version
    // to be VARCHAR in the database.
    
    const handleAddSubmit = async (e) => {
        e.preventDefault();
        const payload = { ...formData, apk_version: formData.apk_version.trim() };

        try {
            await api.post('/downloads', payload); 
            toast.success("Download item created successfully!");
            fetchDownloads();
            closeModal();
        } catch (error) {
            console.error('❌ Error adding item:', error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to add item.";
            toast.error(`Error adding item: ${errorMessage}`);
        }
    };
    
    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!editingDownload) return;

        const payload = { ...formData, apk_version: formData.apk_version.trim() };
        const id = editingDownload.id;

        try {
            await api.put(`/downloads/${id}`, payload);
            toast.success("Download item updated successfully!");
            fetchDownloads();
            closeModal();
        } catch (error) {
            console.error('❌ Error editing item:', error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to update item.";
            toast.error(`Error updating item: ${errorMessage}`);
        }
    };

    const handleDeleteDownload = useCallback(async (id) => {
        if (!window.confirm('Are you sure you want to delete this download item?')) return;
        
        try {
            await api.delete(`/downloads/${id}`);
            toast.success(`Download item ID ${id} deleted!`);
            setDownloads(prev => prev.filter(d => d.id !== id));
        } catch (error) {
            console.error('❌ Error deleting item:', error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to delete item.";
            toast.error(`Error deleting item: ${errorMessage}`);
        }
    }, [api]); 

    // --- Modal Rendering ---
    const renderModal = () => {
        const isEdit = modalType === 'editDownload';
        if (!modalType) return null;

        return (
            <div className="modal-overlay">
                <div className="modal-content">
                    <button type="button" className="close-button" onClick={closeModal}>&times;</button>
                    <form onSubmit={isEdit ? handleEditSubmit : handleAddSubmit}> 
                        <h2>{isEdit ? 'Edit Download Item' : 'Add New Download Item'}</h2>
                        
                        <div>
                            <label>Application Name:</label>
                            <input type="text" name="application_name" value={formData.application_name} onChange={handleFormChange} required />
                        </div>

                        <div className="FormInputGroup">
                            <label>Image Link (Logo):</label>
                            <div className="input-with-button">
                                <input 
                                    type="text" 
                                    name="image_link" 
                                    value={formData.image_link} 
                                    onChange={handleFormChange} 
                                    placeholder="Select from Gallery or Enter URL" 
                                />
                                <button 
                                    type="button" 
                                    className="FormInputGroup-button add-button" 
                                    onClick={openGallery}
                                >
                                    {formData.image_link ? 'Change Image' : 'Select Image'}
                                </button>
                            </div>
                            {formData.image_link && (<p className="item-note">Current Logo: <img src={formData.image_link} alt="Current Logo" style={{ maxWidth: '50px', maxHeight: '50px', verticalAlign: 'middle' }} /></p>)}
                        </div>
                        
                        <div className="FormInputGroup">
                            <label>Download Link:</label>
                            <div className="input-with-button">
                                <input 
                                    type="text" 
                                    name="download_link" 
                                    value={formData.download_link} 
                                    onChange={handleFormChange} 
                                    required 
                                    placeholder="Enter URL manually or click 'Select APK' to auto-populate" 
                                />
                                <button 
                                    type="button" 
                                    className="FormInputGroup-button add-button"
                                    onClick={openApkManager} 
                                >
                                    Select APK
                                </button>
                            </div>
                        </div>
                        
                        <div>
                            <label>Device Type:</label>
                            <select className="selectedProduct" name="device_type" value={formData.device_type} onChange={handleFormChange} required>
                                <option value="">-- Select Device Type --</option>
                                {deviceOptions.map(option => (<option key={option} value={option}>{option}</option>))}
                            </select>
                        </div>
                        
                        <div>
                            <label>APK/App Version:</label>
                            <input 
                                type="text" 
                                name="apk_version" 
                                value={formData.apk_version} 
                                onChange={handleFormChange} 
                                required 
                                pattern="[0-9]+(\.[0-9]+)*"
                                title="Contoh: 1, 1.5, 2.10 atau 1.2.3"
                                placeholder="Enter Version manually (e.g. 1.5) or it will be auto-populated"
                            />
                        </div>

                        <div>
                            <label>Manual Description (Optional):</label>
                            <textarea 
                                name="description" 
                                value={formData.description} 
                                onChange={handleFormChange} 
                                rows="3"
                                placeholder="Add a custom description for this download item"
                            />
                        </div>

                        <button type="submit">{isEdit ? 'Update Item' : 'Add Item'}</button>
                    </form>
                </div>
            </div>
        );
    };

    return (
        <div className="AddProduct">
            <h1>Download Management</h1>
            
            <div className="button-group">
                <button type="button" className="add-button" onClick={() => openModal('addDownload')}>
                    Add Download Item
                </button>
            </div>
            
            <div className="product-page-container">
                <DownloadDisplay downloads={downloads} openModal={openModal} handleDeleteDownload={handleDeleteDownload} />
            </div>
            
            {modalType && renderModal()}
            
            {isGalleryOpen && (
                <Gallery 
                    onSelectImage={handleImageSelect} 
                    onClose={closeGallery} 
                />
            )}
            
            {isApkManagerOpen && (
                <ApkManager 
                    onSelectApk={handleApkSelect} 
                    onClose={closeApkManager} 
                />
            )}
        </div>
    );
};

export default ManageDownload;